import { App } from './app';
import { toAppUrl, UiAppConfig, AppConfigContext } from './app/core';
import {
  getCurrentFeatureCode,
  getCurrentOktaClientCode,
  setCurrentOktaClientCode,
  setCurrentFeatureCode,
  getSignInMountPath,
} from './app/core/auth';
import { OktaAuth, toRelativeUrl } from '@okta/okta-auth-js';
import { AppFeaturesConfig } from '@ssar-app/domain';
import {
  buildPathBuilder,
  buildOktaAuthManager,
  OktaAuthnAppContext,
} from '@ssar-app/okta-authn-react';
import { useState, useCallback, useMemo } from 'react';
import { useHistory } from 'react-router';

type RootAppProps = {
  appConfig: AppFeaturesConfig;
};

const appRootBuildPathBuilder = buildPathBuilder(toAppUrl('/'));

export const RootApp = (props: RootAppProps) => {
  const currentFeatureCode = getCurrentFeatureCode() || undefined;
  const currentOktaClientCode = getCurrentOktaClientCode() || undefined;
  const { appConfig } = props;
  const { oktaClientConfigurations = [] } = appConfig;
  const [oktaClientCode, setOktaClientCodeBase] = useState<string | undefined>(
    currentOktaClientCode,
  );

  const setOktaClientCode = useCallback((oktaClientCode: string) => {
    setCurrentOktaClientCode(oktaClientCode);
    setOktaClientCodeBase(oktaClientCode);
  }, []);

  const [featureCode, setFeatureCodeBase] = useState<string | undefined>(
    currentFeatureCode,
  );

  const setFeatureCode = useCallback((featureCode: string) => {
    setCurrentFeatureCode(featureCode);
    setFeatureCodeBase(featureCode);
  }, []);

  const getCurrentFeature = useCallback(() => {
    const feature = appConfig?.features.find((a) => a.code === featureCode);
    return feature;
  }, [appConfig?.features, featureCode]);

  const uiAppConfig: UiAppConfig = useMemo(() => {
    return {
      ...appConfig,
      setCurrentFeatureCode: setFeatureCode,
      currentFeatureCode: featureCode,
      getCurrentFeature,
    };
  }, [appConfig, featureCode, getCurrentFeature, setFeatureCode]);

  const history = useHistory();
  const oktaAuthManager = useMemo(() => {
    return buildOktaAuthManager(oktaClientConfigurations, {
      storageKey: () => 'ssar-okta-storage-key',
      onAuthRequired() {
        history.push(appRootBuildPathBuilder('/'));
      },
      oktaAuthOptions: {
        redirectUri:
          window.location.origin + toAppUrl('/authorization-code-b2e/callback'),
        async restoreOriginalUri(
          _: OktaAuth,
          originalUri: string | null | undefined,
        ) {
          const basepath = history.createHref({});

          const originalUriWithoutBasepath =
            originalUri?.replace(basepath, '/') || '/';
          const path = toRelativeUrl(
            originalUriWithoutBasepath,
            window.location.origin,
          );
          history.push(path);
        },
      },
    });
  }, [history, oktaClientConfigurations]);

  return (
    <AppConfigContext value={uiAppConfig}>
      <OktaAuthnAppContext
        value={{
          oktaAuthManager,
          oktaClientCode,
          oktaClientConfigurations,
          loginMountPath: getSignInMountPath(),
          setCurrentOktaClientCode: setOktaClientCode,
        }}
      >
        <App></App>
      </OktaAuthnAppContext>
    </AppConfigContext>
  );
};
